import { useState } from 'react';
import { darkTheme, FONT } from '../../theme/tokens';
import { Spinner } from '../ui/Spinner';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { useAgentChat } from './useAgentChat';
import {
  applyScopeLabel,
  buildCancelMessage,
  buildConfirmMessage,
  tidyConfirmTitle,
} from './confirmationMessages';

const t = darkTheme;

interface ConfirmationFooterProps {
  /** Finding the agent is asking to apply. Sourced from
   *  `tool_result.content.proposed_action.finding_id` per the SC-2157 wire
   *  contract; required for disambiguation when multiple confirmations are
   *  pending. */
  findingId: string;
  /** Rich user-facing prompt rendered as the footer body. Sourced from
   *  `tool_result.content.summary` (distinct from the pill's own
   *  `result.summary` which reads as a status line). */
  summary: string;
  /** Targeted business identifiers (SKUs) when the agent scoped the apply to
   *  specific rows; absent → whole-finding apply. From
   *  `proposed_action.entity_ids` (SC-2540). */
  entityIds?: string[];
  /** Total entities the apply touches (SC-2750); drives the whole-finding
   *  "all N" wording. Absent on pre-SC-2750 BE → number-free fallback. */
  affectedCount?: number;
}

type SubmittedAction = 'confirm' | 'cancel';

/**
 * Inline Confirm/Cancel affordance for the SC-2157 apply_finding confirmation
 * round-trip. Renders inside ToolPill.actionFooter when a tool_result carries
 * a ConfirmationRequest. Confirm/Cancel post natural-language user messages
 * that the agent interprets in context of the prior ConfirmationRequest —
 * Option 2 wire protocol per BE Claude.
 */
export function ConfirmationFooter({
  findingId,
  summary,
  entityIds,
  affectedCount,
}: ConfirmationFooterProps) {
  // Scope of the pending apply — "this entity" vs "the whole finding — all N".
  // Rendered above the buttons so the user knows what they're approving.
  const scope = applyScopeLabel(entityIds, affectedCount);
  const { active } = useActiveCatalog();
  // active should always be set when this card mounts (the agent only emits
  // confirmation requests after a catalog is selected), but the empty-string
  // fallback keeps useAgentChat's catalog key valid if a stale render
  // briefly lands here mid-switch.
  /* v8 ignore next */
  const chat = useAgentChat(active?.id ?? '');
  const [submitted, setSubmitted] = useState<SubmittedAction | null>(null);

  // The chat slice has three meaningful statuses (idle / streaming / error),
  // not two. After a click, the card displays:
  //   - submitted + streaming → "Confirming…" / "Cancelling…" with spinner
  //   - submitted + idle      → "✓ Confirmed" / "✕ Cancelled" settled
  //   - submitted + error     → red "Couldn't send" banner with the original
  //                              buttons re-enabled so the user can retry by
  //                              re-clicking Confirm or change their mind to
  //                              Cancel (Greptile P1, SC-2163 re-review)
  // Buttons disable only while a stream is actually in flight — error state
  // keeps them clickable as the retry affordance.
  const isStreaming = chat.status === 'streaming';
  const isErrored = chat.status === 'error';
  const showResolved = submitted !== null && !isErrored;
  const showRetry = submitted !== null && isErrored;

  const handleConfirm = () => {
    // Defense-in-depth: the button is also `disabled={isStreaming}`, so the
    // handler can't normally fire in this state. React swallows synthetic
    // clicks on disabled buttons, so this guard exists for the future case
    // where the disabled prop drifts off (a11y override, custom button impl).
    /* v8 ignore next */
    if (isStreaming) return;
    setSubmitted('confirm');
    void chat.send(buildConfirmMessage(findingId));
  };

  const handleCancel = () => {
    // See handleConfirm above for the defense-in-depth rationale.
    /* v8 ignore next */
    if (isStreaming) return;
    setSubmitted('cancel');
    void chat.send(buildCancelMessage(findingId));
  };

  return (
    <div
      data-testid="confirmation-footer"
      data-finding-id={findingId}
      style={{
        background: t.surfaceAlt,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        fontFamily: FONT,
      }}
    >
      {/* The scope is part of the question, not a control: a muted sentence in
          the title block, with no glyph and no row of its own. As a glyph-led
          line above Confirm it read as a button you had to click to pick
          "all entities" (SC-3516). Hidden once resolved, like the buttons. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ color: t.text, fontSize: 13, lineHeight: 1.4 }}>{tidyConfirmTitle(summary)}</div>
        {!(showResolved && submitted !== null) && (
          <p
            data-testid="confirmation-footer-scope"
            style={{ margin: 0, color: t.text3, fontSize: 12, lineHeight: 1.4 }}
          >
            {scope}
          </p>
        )}
      </div>
      {showResolved && submitted !== null ? (
        <ResolvedStatus action={submitted} streaming={isStreaming} />
      ) : (
        <>
          {showRetry && (
            <div
              data-testid="confirmation-footer-error"
              role="alert"
              style={{
                color: t.redText,
                fontSize: 12,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span aria-hidden="true">✕</span>
              <span>Couldn&rsquo;t send — try again</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <FooterButton
              variant="primary"
              disabled={isStreaming}
              onClick={handleConfirm}
              label="Confirm"
              testId="confirmation-footer-confirm"
            />
            <FooterButton
              variant="neutral"
              disabled={isStreaming}
              onClick={handleCancel}
              label="Cancel"
              testId="confirmation-footer-cancel"
            />
          </div>
        </>
      )}
    </div>
  );
}

interface ResolvedStatusProps {
  action: SubmittedAction;
  streaming: boolean;
}

function ResolvedStatus({ action, streaming }: ResolvedStatusProps) {
  const isConfirm = action === 'confirm';
  const tone = isConfirm
    ? { color: t.accentText, glyph: '✓' }
    : { color: t.text3, glyph: '✕' };
  const label = streaming
    ? isConfirm
      ? 'Confirming…'
      : 'Cancelling…'
    : isConfirm
      ? 'Confirmed'
      : 'Cancelled';
  return (
    <div
      data-testid="confirmation-footer-status"
      data-action={action}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: tone.color,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {streaming ? (
        <Spinner size={12} />
      ) : (
        <span aria-hidden="true" style={{ fontSize: 13 }}>
          {tone.glyph}
        </span>
      )}
      <span>{label}</span>
    </div>
  );
}

interface FooterButtonProps {
  variant: 'primary' | 'neutral';
  disabled: boolean;
  onClick: () => void;
  label: string;
  testId: string;
}

function FooterButton({
  variant,
  disabled,
  onClick,
  label,
  testId,
}: FooterButtonProps) {
  const isPrimary = variant === 'primary';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 14px',
        borderRadius: 6,
        border: `1px solid ${isPrimary ? t.blue : t.border2}`,
        background: isPrimary ? t.blueBg : 'transparent',
        color: isPrimary ? t.blueText : t.text,
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );
}
